import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  addSupportWatcher,
  assignSupportTicket,
  removeSupportWatcher,
  updateSupportTicket,
  updateSupportTicketNotification,
  updateSupportTicketStatus,
} from '../api/authApi';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';
import { StatusBadge } from './StatusBadge';
import { SupportInterestedUsersAvatars } from './SupportInterestedUsersAvatars';
import { colorTokens, radiusTokens } from '../app/designTokens';

const { semantic } = colorTokens;

export const TICKET_STATUSES = [
  'open',
  'waiting_for_support',
  'waiting_for_customer',
  'resolved',
  'closed',
];

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

function ContextField({ label, value }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="caption" sx={{ color: semantic.text.muted }} display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word', color: semantic.text.primary }}>
        {value}
      </Typography>
    </Box>
  );
}

function PaperSection({ title, children }) {
  return (
    <Box
      component="section"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: `${radiusTokens.card}px`,
        border: `1px solid ${semantic.border.subtle}`,
        bgcolor: semantic.bg.surface,
      }}
    >
      <Typography variant="subtitle2" gutterBottom sx={{ color: semantic.text.primary }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function deadlineInputValue(deadline) {
  if (!deadline) return '';
  const raw = String(deadline);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  try {
    return new Date(raw).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

/**
 * Operator workflow controls for Support Detail right panel.
 * Immediate-save model (parity with MafertApp onChange saves).
 */
export function SupportTicketWorkflowPanel({
  ticket,
  displayId,
  emptyValue,
  formatDate,
  formFields = [],
  sla,
  watchers = [],
  assignees = [],
  canReply,
  canAssign,
  canClose,
  currentUserId,
  savingField,
  onSavingChange,
  onTicketUpdated,
  onWatchersRefresh,
  onError,
  onMarkUnread,
  markUnreadBusy,
}) {
  const { t } = useTranslation(['support', 'common', 'statuses']);
  const [watcherUserId, setWatcherUserId] = useState('');
  const [confirmDeleteWatcher, setConfirmDeleteWatcher] = useState(null);

  const isBusy = (field) => savingField === field;
  const anySaving = Boolean(savingField);

  const awaitingOptions = useMemo(() => {
    const map = new Map();
    assignees.forEach((u) => map.set(String(u.id), u));
    if (ticket.requested_by?.id) {
      map.set(String(ticket.requested_by.id), {
        id: ticket.requested_by.id,
        name: ticket.requested_by.name || ticket.requested_by_name,
      });
    }
    if (ticket.awaiting_user?.id && !map.has(String(ticket.awaiting_user.id))) {
      map.set(String(ticket.awaiting_user.id), ticket.awaiting_user);
    }
    return Array.from(map.values());
  }, [assignees, ticket]);

  const selfWatcher = watchers.find((w) => Number(w.user_id) === Number(currentUserId));
  const notificationsEnabled = selfWatcher
    ? Boolean(selfWatcher.notification) && !selfWatcher.muted_at
    : null;

  const watcherCandidates = assignees.filter(
    (u) => !watchers.some((w) => Number(w.user_id) === Number(u.id))
  );

  const run = async (field, action) => {
    if (savingField) return;
    onSavingChange?.(field);
    try {
      await action();
    } catch (err) {
      const message = safeApiErrorMessage(err, t('support:workflow_update_failed'));
      onError?.(message);
    } finally {
      onSavingChange?.(null);
    }
  };

  const handleStatusChange = (event) => {
    const next = event.target.value;
    if (next === ticket.status || anySaving) return;
    const needsClose = next === 'resolved' || next === 'closed';
    if (needsClose && !canClose) {
      onError?.(t('support:workflow_permission_denied'));
      return;
    }
    if (!needsClose && !canReply) {
      onError?.(t('support:workflow_permission_denied'));
      return;
    }
    run('status', async () => {
      const updated = await updateSupportTicketStatus(ticket.id, { status: next });
      onTicketUpdated?.(updated, 'status');
    });
  };

  const handlePriorityChange = (event) => {
    const next = event.target.value;
    if (next === ticket.priority || anySaving || !canReply) return;
    run('priority', async () => {
      const updated = await updateSupportTicket(ticket.id, { priority: next });
      onTicketUpdated?.(updated, 'priority');
    });
  };

  const handleAssigneeChange = (event) => {
    const raw = event.target.value;
    const nextId = raw === '' ? null : Number(raw);
    const current = ticket.assigned_to_user_id ?? ticket.assigned_to?.id ?? null;
    if (String(nextId ?? '') === String(current ?? '') || anySaving || !canAssign) return;
    run('assignee', async () => {
      const updated = await assignSupportTicket(ticket.id, { assigned_to_user_id: nextId });
      onTicketUpdated?.(updated, 'assignee');
    });
  };

  const handleAwaitingChange = (event) => {
    const raw = event.target.value;
    const nextId = raw === '' ? null : Number(raw);
    const current = ticket.awaiting_user_id ?? ticket.awaiting_user?.id ?? null;
    if (String(nextId ?? '') === String(current ?? '') || anySaving || !canAssign) return;
    run('awaiting', async () => {
      const updated = await assignSupportTicket(ticket.id, { awaiting_user_id: nextId });
      onTicketUpdated?.(updated, 'awaiting');
    });
  };

  const handleDeadlineChange = (event) => {
    const next = event.target.value || null;
    const current = deadlineInputValue(ticket.deadline) || null;
    if (String(next ?? '') === String(current ?? '') || anySaving || !canReply) return;
    run('deadline', async () => {
      const updated = await updateSupportTicket(ticket.id, { deadline: next });
      onTicketUpdated?.(updated, 'deadline');
    });
  };

  const handleClearDeadline = () => {
    if (!ticket.deadline || anySaving || !canReply) return;
    run('deadline', async () => {
      const updated = await updateSupportTicket(ticket.id, { deadline: null });
      onTicketUpdated?.(updated, 'deadline');
    });
  };

  const handleAddWatcher = () => {
    const userId = Number(watcherUserId);
    if (!userId || !canAssign || anySaving) return;
    run('watchers', async () => {
      await addSupportWatcher(ticket.id, { user_id: userId, notification: true });
      setWatcherUserId('');
      await onWatchersRefresh?.();
    });
  };

  const handleRemoveWatcher = (userId) => {
    if (!canAssign || anySaving) return;
    run('watchers', async () => {
      await removeSupportWatcher(ticket.id, userId);
      setConfirmDeleteWatcher(null);
      await onWatchersRefresh?.();
    });
  };

  const handleToggleNotifications = () => {
    if (!currentUserId || !canReply || anySaving || notificationsEnabled === null) return;
    const next = !notificationsEnabled;
    run('notifications', async () => {
      await updateSupportTicketNotification(ticket.id, {
        user_id: Number(currentUserId),
        notification: next,
      });
      await onWatchersRefresh?.();
    });
  };

  return (
    <Box data-testid="ticket-context-panel">
      <PaperSection title={t('support:context_panel')}>
        <ContextField label={t('support:public_number')} value={displayId || emptyValue} />
        <ContextField label={t('common:customer')} value={ticket.customer?.name || emptyValue} />
        <ContextField label={t('common:tenant')} value={ticket.tenant?.name || emptyValue} />
        <ContextField
          label={t('support:requester')}
          value={ticket.requested_by?.name || ticket.requested_by_name || emptyValue}
        />
        <ContextField label={t('common:source')} value={ticket.source || emptyValue} />
        <ContextField label={t('common:type')} value={ticket.form_type || ticket.type || emptyValue} />
        <ContextField label={t('support:mask_number_label')} value={ticket.mask_number || emptyValue} />
        <ContextField label={t('support:created')} value={formatDate(ticket.created_at)} />
        <ContextField label={t('support:updated')} value={formatDate(ticket.updated_at)} />
        <ContextField label={t('support:resolved_at')} value={formatDate(ticket.resolved_at)} />
        <ContextField label={t('support:closed_at')} value={formatDate(ticket.closed_at)} />
      </PaperSection>

      <PaperSection title={t('support:workflow_panel')}>
        <Stack spacing={1.5}>
          <Box>
            <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{t('common:status')}</Typography>
              {isBusy('status') && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Stack>
            {(canReply || canClose) ? (
              <FormControl fullWidth size="small" disabled={anySaving}>
                <Select
                  native
                  value={ticket.status || ''}
                  onChange={handleStatusChange}
                  inputProps={{ 'aria-label': t('common:status'), 'data-testid': 'workflow-status' }}
                >
                  {TICKET_STATUSES.map((status) => {
                    const needsClose = status === 'resolved' || status === 'closed';
                    const allowed = needsClose ? canClose : canReply;
                    return (
                      <option key={status} value={status} disabled={!allowed && status !== ticket.status}>
                        {t(`statuses:support.${status}`, { defaultValue: status })}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
            ) : (
              <StatusBadge status={ticket.status} category="support" />
            )}
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{t('support:tickets.priority')}</Typography>
              {isBusy('priority') && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Stack>
            {canReply ? (
              <FormControl fullWidth size="small" disabled={anySaving}>
                <Select
                  native
                  value={ticket.priority || 'normal'}
                  onChange={handlePriorityChange}
                  inputProps={{ 'aria-label': t('support:tickets.priority'), 'data-testid': 'workflow-priority' }}
                >
                  {PRIORITIES.map((priority) => (
                    <option key={priority} value={priority}>
                      {t(`statuses:support_priority.${priority}`, { defaultValue: priority })}
                    </option>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <StatusBadge status={ticket.priority} category="priority" />
            )}
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{t('support:assigned_to')}</Typography>
              {isBusy('assignee') && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Stack>
            {canAssign ? (
              <FormControl fullWidth size="small" disabled={anySaving}>
                <Select
                  native
                  value={ticket.assigned_to?.id ? String(ticket.assigned_to.id) : ''}
                  onChange={handleAssigneeChange}
                  inputProps={{ 'aria-label': t('support:assigned_to'), 'data-testid': 'workflow-assignee' }}
                >
                  <option value="">{t('support:unassigned')}</option>
                  {assignees.map((user) => (
                    <option key={user.id} value={String(user.id)}>{user.name || user.email}</option>
                  ))}
                  {ticket.assigned_to?.id && !assignees.some((u) => Number(u.id) === Number(ticket.assigned_to.id)) && (
                    <option value={String(ticket.assigned_to.id)}>{ticket.assigned_to.name}</option>
                  )}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2">{ticket.assigned_to?.name || emptyValue}</Typography>
            )}
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{t('support:awaiting_user')}</Typography>
              {isBusy('awaiting') && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Stack>
            {canAssign ? (
              <FormControl fullWidth size="small" disabled={anySaving}>
                <Select
                  native
                  value={ticket.awaiting_user?.id ? String(ticket.awaiting_user.id) : ''}
                  onChange={handleAwaitingChange}
                  inputProps={{ 'aria-label': t('support:awaiting_user'), 'data-testid': 'workflow-awaiting' }}
                >
                  <option value="">{t('support:awaiting_none')}</option>
                  {awaitingOptions.map((user) => (
                    <option key={user.id} value={String(user.id)}>{user.name || user.email}</option>
                  ))}
                </Select>
                <FormHelperText>{t('support:awaiting_help')}</FormHelperText>
              </FormControl>
            ) : (
              <Typography variant="body2">{ticket.awaiting_user?.name || emptyValue}</Typography>
            )}
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{t('support:deadline')}</Typography>
              {isBusy('deadline') && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Stack>
            {canReply ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={deadlineInputValue(ticket.deadline)}
                  onChange={handleDeadlineChange}
                  disabled={anySaving}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'aria-label': t('support:deadline'), 'data-testid': 'workflow-deadline' }}
                />
                <Button size="small" onClick={handleClearDeadline} disabled={!ticket.deadline || anySaving}>
                  {t('support:clear_deadline')}
                </Button>
              </Stack>
            ) : (
              <Typography variant="body2">{ticket.deadline || emptyValue}</Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onMarkUnread?.()}
              disabled={markUnreadBusy || anySaving}
              data-testid="mark-unread-button"
            >
              {markUnreadBusy ? t('support:marking_unread') : t('support:mark_unread')}
            </Button>
            {notificationsEnabled !== null && canReply && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleToggleNotifications}
                disabled={anySaving}
                data-testid="toggle-notifications"
              >
                {notificationsEnabled
                  ? t('support:notifications.disable')
                  : t('support:notifications.enable')}
              </Button>
            )}
          </Stack>
          {notificationsEnabled === null && (
            <Typography variant="caption" color="text.secondary">
              {t('support:notifications.watch_first')}
            </Typography>
          )}
        </Stack>
      </PaperSection>

      {formFields.length > 0 && (
        <PaperSection title={t('support:form.form_data')}>
          {formFields.map((field) => (
            <ContextField key={field.key} label={field.label} value={field.value || emptyValue} />
          ))}
        </PaperSection>
      )}

      {sla && (
        <PaperSection title={t('support:sla')}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('support:first_response_policy_help')}
          </Typography>
          <Typography variant="body2">
            {t('support:first_response_due')}: {formatDate(sla.first_response_due_at_local || sla.first_response_due_at)}
          </Typography>
          <Typography variant="body2">{t('support:first_response_at')}: {formatDate(sla.first_response_at)}</Typography>
          {sla.resolution_due_at ? (
            <Typography variant="body2" color="text.secondary">
              {t('support:resolution_due_internal')}: {formatDate(sla.resolution_due_at)}
            </Typography>
          ) : null}
          {sla.first_response_breached && (
            <Chip label={t('support:first_response_breached')} color="error" size="small" sx={{ mt: 1 }} />
          )}
          {sla.resolution_breached && (
            <Chip label={t('support:resolution_breached')} color="error" size="small" sx={{ mt: 1, ml: 1 }} />
          )}
        </PaperSection>
      )}

      <PaperSection title={t('support:interested_users')}>
        <Box sx={{ mb: watchers.length ? 1 : 0, maxWidth: '100%', overflow: 'hidden' }}>
          <SupportInterestedUsersAvatars
            users={watchers}
            emptyLabel={t('support:no_interested_users')}
          />
        </Box>
        {canAssign && watchers.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1 }} data-testid="interested-users-manage-list">
            {watchers.map((w) => (
              <Stack
                key={w.user_id}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="body2" sx={{ color: semantic.text.secondary, wordBreak: 'break-word' }}>
                  {w.user?.name || w.user?.email || t('support:unknown_author')}
                </Typography>
                <Button size="small" onClick={() => setConfirmDeleteWatcher(w)} disabled={anySaving}>
                  {t('support:remove')}
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
        {canAssign && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }} alignItems={{ sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 0, flex: 1 }} disabled={anySaving}>
              <InputLabel shrink htmlFor="add-watcher-select">{t('support:add_interested_user')}</InputLabel>
              <Select
                native
                id="add-watcher-select"
                value={watcherUserId}
                onChange={(e) => setWatcherUserId(e.target.value)}
                inputProps={{ 'data-testid': 'add-watcher-select' }}
              >
                <option value="">{t('support:add_interested_user')}</option>
                {watcherCandidates.map((user) => (
                  <option key={user.id} value={String(user.id)}>{user.name || user.email}</option>
                ))}
              </Select>
            </FormControl>
            <Button size="small" variant="contained" onClick={handleAddWatcher} disabled={!watcherUserId || anySaving}>
              {t('support:add_interested_user')}
            </Button>
          </Stack>
        )}
        {isBusy('watchers') && <CircularProgress size={14} sx={{ mt: 1 }} />}
      </PaperSection>

      <Dialog open={Boolean(confirmDeleteWatcher)} onClose={() => setConfirmDeleteWatcher(null)}>
        <DialogTitle>{t('support:remove_interested_user_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('support:remove_interested_user_confirm', {
              name: confirmDeleteWatcher?.user?.name || confirmDeleteWatcher?.user?.email || t('support:unknown_author'),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteWatcher(null)}>{t('common:cancel')}</Button>
          <Button
            color="error"
            onClick={() => handleRemoveWatcher(confirmDeleteWatcher.user_id)}
            disabled={anySaving}
          >
            {t('support:remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
